import { Injectable } from "@angular/core";
import { I18NextPipe } from "angular-i18next";
import { preferenceColors, preferenceFontSizes, preferenceTitles } from "src/app/models";
import { PercentageFields, TimeObject } from "../widget-helpers/helpers";
import { PreferenceUtilitiesService } from "./preference-utilities.service";

let titles: preferenceTitles = {
    legendTitle: "",
    xTitle: "",
    yTitle: ""
};

let fontSizes: preferenceFontSizes = {
    legendFontSize: "",
    legendElementsFontSize: "",
    xTitleFontSize: "",
    xLabelsFontSize: "",
    yTitleFontSize: "",
    yLabelsFontSize: ""
};

let colors: preferenceColors = {
    legendColor: "",
    legendElementsColor: "",
    xTitleColor: "",
    xLabelsColor: "",
    yTitleColor: "",
    yLabelsColor: ""
};

@Injectable({
    providedIn: 'root'
})
export class ChartService {
    constructor(private i18nextPipe: I18NextPipe, private preferenceUtilitiesService: PreferenceUtilitiesService) { }

    createChart(gridItem, categories, series, props, type, theme, isCalls, yTitle?): any {

        this.resetPreferences();
        this.checkForYTitle(yTitle);

        titles = this.preferenceUtilitiesService.columnSettings(gridItem, titles);
        fontSizes = this.preferenceUtilitiesService.setFontSizes(gridItem);
        colors = this.preferenceUtilitiesService.setColors(gridItem);

        let here = this;

        return {
            yAxis: {
                opposite: false,
                allowDecimals: false,
                title: {
                    text: titles.yTitle,
                    style: {
                        fontSize: fontSizes.yTitleFontSize,
                        color: colors.yTitleColor
                    }
                },
                labels: {
                    formatter: function () {
                        let value = this.value;
                        let selectedProperty = props[this.chart.colorCounter - 1];
                        let series = this.chart.series;
                        let formats = [];

                        if (!isCalls) {
                            for (let i = 0; i < props.length; i++) {
                                if (series[i].visible) {
                                    if (TimeObject[type].hasOwnProperty(props[i])) {
                                        if (formats.indexOf("time") === -1) { 
                                            formats.push("time") 
                                        }
                                    } else if (PercentageFields.indexOf(props[i]) > -1) {
                                        if (formats.indexOf("percentage") === -1) { 
                                            formats.push("percentage") 
                                        }
                                    } else {
                                        if (formats.indexOf("digit") === -1) {
                                            formats.push("digit");
                                        }
                                    }
                                }
                            }
    
                            if (type && formats.length === 1 && formats[0] === "time") {
                                if (TimeObject[type].hasOwnProperty(selectedProperty)) {
                                    value = TimeObject[type][selectedProperty](value);
                                    let index = value.toString().indexOf("d");
                                    if (index > -1) {
                                        let valueTokens = value.toString().split(" ");
                                        let days = valueTokens[0];
                                        let daysNumber = days.substring(0, index);
                                        let hours = valueTokens[1].split(":")[0];
                                        if (+hours > 0) {
                                            value = parseInt(daysNumber, 10) + "d" + " " + parseInt(hours, 10) + "h";
                                        } else {
                                            value = daysNumber + "d";
                                        }
                                    } else {
                                        let hours = value.toString().split(":")[0];
                                        let minutes = value.toString().split(":")[1];
                                        let seconds = value.toString().split(":")[2];
    
                                        value = +seconds > 0 ? parseInt(seconds, 10) + "s" : 0;
                                        value = +minutes > 0 ? parseInt(minutes, 10) + "m " + parseInt(seconds, 10) + "s" : value;
                                        value = +hours > 0 ? parseInt(hours, 10) + "h " + parseInt(minutes, 10) + "m" : value;
                                    }
                                }
                            }
                        }

                        return "<span>" + value + "</span>";
                    },
                    style: {
                        fontSize: fontSizes.yLabelsFontSize,
                        color: colors.yLabelsColor
                    }
                }
            },
            xAxis: {
                categories: categories,
                title: {
                    text: titles.xTitle,
                    style: {
                        fontSize: fontSizes.xTitleFontSize,
                        color: colors.xTitleColor
                    }
                },
                crosshair: false,
                labels: {
                    formatter: function () {
                        if (categories.length > 0) {
                            return categories[this.value]?.toString();
                        } else {
                            return "";
                        }
                    },
                    style: {
                        fontSize: fontSizes.xLabelsFontSize,
                        color: colors.xLabelsColor,
                        textOverflow: 'ellipsis'
                    },
                }
            },
            series: series,
            legend: {
                enabled: true,
                title: {
                    text: titles.legendTitle,
                    style: {
                        fontSize: fontSizes.legendFontSize,
                        color: colors.legendColor
                    }
                },
                itemStyle: {
                    fontFamily: 'Quicksand',
                    fontSize: fontSizes.legendElementsFontSize,
                    color: colors.legendElementsColor
                }
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            rangeSelector: {
                enabled: false
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: true,
                        radius: 4
                    }
                }
            },
            navigator: {
                enabled: false,
                xAxis: {
                    labels: {
                        formatter: function (event) {
                            return "";
                        }
                    }
                }
            },
            scrollbar: {
                enabled: false
            },
            tooltip: {
                split: false,
                shared: true,
                pointFormatter: function () {
                    let currentSerie: any = this.series;
                    let tooltip = "";
                    let value = this.y;
                    let index = currentSerie.index;
                    let name = props[index];
                    if (type) {
                        if (TimeObject[type].hasOwnProperty(name)) {
                            value = TimeObject[type][name](value);
                        }
                    }

                    if (isCalls) {
                        let category = categories[this.category];
                        tooltip = `${category}: ${value} ${here.i18nextPipe.transform(`dashboard:${name}`)}`;
                    } else {
                        tooltip = `${here.i18nextPipe.transform(`dashboard:${name}`)}: ${value}`;
                        if (PercentageFields.indexOf(name) > -1) {
                            tooltip = tooltip + "%";
                        }
                    }

                    return `<br/><span style="color:${currentSerie.color}">` + tooltip + '</span>';
                },
            },
            colors: theme.colors,
        };
    }

    generateChartData(data, type, isCalls) {
        let categories = [];
        let series = [];
        let props = [];
        if (data?.length > 0) {
            if (isCalls) {
                let keys = Object.keys(data[0]);
                let valueKeys = keys.filter(function (value, index, arr) {
                    return value.toLowerCase() !== "timestamp";
                });

                let offeredSeriesData = [];
                let answeredSeriesData = [];

                for (let i = 0; i < data.length; i++) {
                    let date = new Date(data[i]["timestamp"]);
                    let hours = date.getHours().toString().length === 1 ? "0" + date.getHours().toString() : date.getHours().toString();
                    let minutes = date.getMinutes().toString().length === 1 ? "0" + date.getMinutes().toString() : date.getMinutes().toString();
                    categories.push(`${hours}:${minutes}`);

                    for (let j = 0; j < valueKeys.length; j++) {
                        if (valueKeys[j] === "callsOffered") {
                            offeredSeriesData.push(data[i]["callsOffered"]);
                        } else {
                            answeredSeriesData.push(data[i]["callsAnswered"]);
                        }
                    }
                }

                if (offeredSeriesData.length > 0) {
                    props.push("offered");
                    series.push({
                        name: this.i18nextPipe.transform('dashboard:offered'),
                        type: type,
                        animation: false,
                        data: offeredSeriesData
                    });
                }

                if (answeredSeriesData.length > 0) {
                    props.push("answered");
                    series.push({
                        name: this.i18nextPipe.transform('dashboard:answered'),
                        type: type,
                        animation: false,
                        data: answeredSeriesData
                    });
                }

            } else {
                let keys = Object.keys(data[0])
                props = keys.filter(function (value, index, arr) {
                    return value.toLowerCase() !== "id" && value.toLowerCase() !== "name" && value.toLocaleLowerCase() !== "alarmflag";
                });

                for (let i = 0; i < props.length; i++) {
                    let propName = props[i];
                    let translatedName = this.i18nextPipe.transform('dashboard:' + propName)
                    series.push({ name: translatedName, data: [], type: type, animation: false });
                    for (let j = 0; j < data.length; j++) {
                        series[i].data.push(data[j][propName] ?? 0);
                    }
                }

                for (let i = 0; i < data.length; i++) {
                    categories.push(data[i]["Name"]);
                }
            }
        }

        return {
            categories,
            series,
            props
        };
    }

    private checkForYTitle(title) {
        if (title) {
            titles.yTitle = title;
        }
    }

    private resetPreferences() {
        titles.legendTitle = "";
        titles.xTitle = "";
        titles.yTitle = "";

        fontSizes.legendFontSize = "";
        fontSizes.legendElementsFontSize = "";
        fontSizes.xTitleFontSize = "";
        fontSizes.xLabelsFontSize = "";
        fontSizes.yTitleFontSize = "";
        fontSizes.yLabelsFontSize = "";

        colors.legendColor = "";
        colors.legendElementsColor = "";
        colors.xTitleColor = "";
        colors.xLabelsColor = "";
        colors.yTitleColor = "";
        colors.yLabelsColor = "";
    }
}
